export interface IRequestOptions {
  method: string;
  async: boolean;
  headers: {
    Accept: string;
    [key: string]: string;
  };
  credentials: 'same-origin' | 'include' | 'omit';
  onProgress?: (percent: number) => void;
  onSuccess?: () => void;
}

export interface IRequestConfig<T> extends Partial<IRequestOptions> {
  data?: T;
}

const DEFAULT_CONFIG: IRequestOptions = {
  method: 'GET',
  async: true,
  headers: {
    Accept: 'application/json',
  },
  credentials: 'same-origin',
};

const request = function <T = any>(
  url: string,
  config: IRequestConfig<T> = DEFAULT_CONFIG,
): Promise<string> {
  const { method, async, headers, data, credentials, onProgress, onSuccess } = config;
  return new Promise((resolve, reject) => {
    let xhr: XMLHttpRequest | any;
    try {
      xhr =
        typeof XMLHttpRequest !== 'undefined'
          ? new XMLHttpRequest()
          : new (window as any).ActiveXObject('Microsoft.XMLHttp');
      xhr.open(method, url, async);

      if (headers) {
        Object.keys(headers).forEach(key => {
          xhr.setRequestHeader(key, headers[key]);
        });
      }

      if (credentials === 'include') {
        xhr.withCredentials = true;
      } else if (credentials === 'omit') {
        xhr.withCredentials = false;
      }

      xhr.onreadystatechange = function () {
        if (xhr.readyState !== 4) {
          return;
        }
        if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 304) {
          resolve(xhr.responseText);
          onSuccess?.();
        } else {
          reject(new Error(xhr.responseText));
        }
      };

      xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress?.(percent);
        }
      });

      data ? xhr.send(data) : xhr.send();
    } catch (error) {
      reject(error);
    }
  });
};

export default request;
